import React, { useCallback, useMemo, useRef } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
import { Box, Text } from '@adsk/alloy-react-box';
import Checkbox from '@adsk/alloy-react-checkbox';
import { keyBy } from '@adsk/alloy-react-helpers';
import { DotSpokeThreeIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import {
  useTable,
  TableRow,
  TableCell,
  LoadMoreDataFooter,
} from '@adsk/alloy-react-table';
import Theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { FEATURE_FLAGS, ENTITIES_MAPPER_KEY } from '../helpers/constants';
import { getFormTemplateUrn } from '../helpers/utils';
import { usePlangridFeatureFlag } from '../PlangridLaunchDarklyProvider';

const TemplateName = styled.div`
  ${{ ...Theme.typography.labelMedium }};
  color: ${(p) =>
    p.disabled ? Theme.colors.charcoal500 : Theme.colors.charcoal900};

  display: ${(p) => (p.isComponent ? 'flex' : 'inherit')};
  align-items: ${(p) => (p.isComponent ? 'center' : 'inherit')};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const FolderName = styled.div`
  ${{ ...Theme.typography.bodySmall }}
  color: ${Theme.colors.charcoal500};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: every hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-spacing: 0;
  text-align: left;
`;

const STYLES = {
  DotSpokeThreeIcon: { marginLeft: 8 },
  TableCell: { paddingRight: 0 },
  LoadMoreDataFooter: { paddingLeft: '50px', justifyContent: 'flex-start' },
};

const FormTemplatesListView = ({
  entities,
  disabledIds,
  selectedIds,
  onLoadMore,
  onChange,
  setSelection,
  multiple,
  loading,
  error,
  onTryAgain,
  dataViewProps,
  isLoadingFolders,
  folders,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const projectIsTemplate = useCurrentProjectIsTemplate();
  const { value: isLibraryComponentsEnabled } = usePlangridFeatureFlag(
    FEATURE_FLAGS.ENABLE_LIBRARY_COMPONENTS,
    false,
  );

  const tableRef = useRef();

  const [disabledIdsWithInherited, selectedIdsWithInherited] = useMemo(() => {
    const inheritedTemplateIds =
      dataViewProps && dataViewProps.inheritedTemplateIds
        ? dataViewProps.inheritedTemplateIds.map(getFormTemplateUrn)
        : [];

    const disabledLibraryTemplateIds =
      projectIsTemplate && !isLibraryComponentsEnabled
        ? entities
            .filter((entity) => Boolean(entity.is_component))
            .map((entity) => entity.id)
        : [];

    return [
      [...disabledIds, ...disabledLibraryTemplateIds, ...inheritedTemplateIds],
      [...selectedIds, ...inheritedTemplateIds],
    ];
  }, [
    disabledIds,
    selectedIds,
    dataViewProps,
    projectIsTemplate,
    isLibraryComponentsEnabled,
    entities,
  ]);

  const ListItem = useCallback(
    ({ rowData, disabledIds }) => {
      const disabled =
        disabledIds.includes(rowData.id) ||
        (rowData.is_component &&
          projectIsTemplate &&
          !isLibraryComponentsEnabled);

      return (
        <Box width="100%">
          <Tooltip
            content={
              rowData.is_component &&
              projectIsTemplate &&
              !isLibraryComponentsEnabled
                ? T.translate('LIBRARY_FORMS_NOT_SUPPORTED')
                : null
            }
          >
            <TemplateName
              isComponent={rowData.is_component}
              disabled={disabled}
            >
              {rowData.name}
              {rowData.is_component && (
                <DotSpokeThreeIcon size={20} style={STYLES.DotSpokeThreeIcon} />
              )}
            </TemplateName>
          </Tooltip>
          <FolderName>
            {!isLoadingFolders &&
              folders?.find((folder) => folder.uid === rowData.folder_uid)
                ?.name}
          </FolderName>
        </Box>
      );
    },
    [
      folders,
      isLoadingFolders,
      projectIsTemplate,
      isLibraryComponentsEnabled,
      T,
    ],
  );

  const rowSelection = useMemo(
    () =>
      selectedIdsWithInherited?.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {}) ?? {},
    [selectedIdsWithInherited],
  );

  const columns = useMemo(
    () => [
      ...(multiple
        ? [
            {
              id: 'select',
              size: 12,
              minSize: 12,
              cell: ({
                row: { getIsSomeSelected, getIsSelected, getCanSelect },
              }) => (
                <Checkbox
                  checked={
                    getIsSomeSelected()
                      ? Checkbox.INDETERMINATE
                      : getIsSelected()
                  }
                  disabled={!getCanSelect()}
                />
              ),
            },
          ]
        : []),
      {
        id: 'TEMPLATE_NAME',
        accessorKey: 'TEMPLATE_NAME',
        size: 230,
        minSize: 230,
        cell: ({ row }) => (
          <ListItem
            rowData={row.original}
            disabledIds={disabledIdsWithInherited}
          />
        ),
      },
    ],
    [disabledIdsWithInherited, multiple],
  );

  const dataByKey = useMemo(
    () => keyBy(entities, ENTITIES_MAPPER_KEY),
    [entities],
  );

  const table = useReactTable({
    columns,
    data: entities,
    getRowId: (row) => row?.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => !disabledIdsWithInherited?.includes(row.id),
    enableMultiRowSelection: multiple,
    enableColumnResizing: false,
    enableSortingRemoval: false,
    enableSorting: false,
    state: {
      rowSelection,
    },
  });

  const { getTableRowProps, getTableCellProps } = useTable({ table });

  const { getRowModel } = table;

  const { rows } = getRowModel();

  const handleScroll = useCallback(async () => {
    if (tableRef?.current && !error && !loading) {
      const { scrollTop, scrollHeight, offsetHeight } = tableRef?.current;
      if (Math.round(scrollTop) === Math.round(scrollHeight - offsetHeight)) {
        await onLoadMore?.();
      }
    }
  }, [error, onLoadMore, loading]);

  const handleRowSelection = ({ getCanSelect, getIsSelected, id }) => {
    if (!getCanSelect?.()) return;
    if (multiple) {
      onChange({
        entity: dataByKey[id],
        checked: !getIsSelected?.(),
      });
    } else {
      setSelection(
        selectedIdsWithInherited.includes(id) ? [] : [dataByKey[id]],
      );
    }
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="auto"
      onScroll={handleScroll}
      ref={tableRef}
    >
      <StyledTable>
        <tbody>
          {rows.map((row) => {
            const { id, getCanSelect } = row;
            return (
              <TableRow
                key={id}
                {...getTableRowProps(row)}
                onClick={() => handleRowSelection(row)}
                disabled={!getCanSelect()}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    {...getTableCellProps(cell)}
                    style={STYLES.TableCell}
                  />
                ))}
              </TableRow>
            );
          })}
        </tbody>
      </StyledTable>
      {(loading || error) && (
        <LoadMoreDataFooter
          isLoading
          onLoad={onLoadMore}
          hasError={!!error}
          onRetry={onTryAgain}
          style={STYLES.LoadMoreDataFooter}
          renderLoading={() => (
            <>
              <ProgressRing size={ProgressRing.SIZES.SMALL} />
              <Text
                typography="bodySmall"
                color="charcoal700"
                marginLeft="13px"
              >
                {T.translate('LOADING_MORE')}
              </Text>
            </>
          )}
        />
      )}
    </Box>
  );
};

FormTemplatesListView.displayName = 'FormTemplatesListView';

FormTemplatesListView.propTypes = {
  /**
   * Whether is loading Forms from BE.
   */
  loading: PropTypes.bool,
  /**
   * IDs of selected Form entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of Form entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of entities.
   */
  entities: PropTypes.arrayOf(PropTypes.object),
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.shape({
    inheritedTemplateIds: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default FormTemplatesListView;
